import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconAudiocall, IconVideocall, IconVideoOutgoingCall, IconAudioOutgoingCall, IconVideoIncommingCall, IconAudioIncommingCall, IconAudioMissedCall, IconVideoMissedCall } from '../../../assets/images';
import { formatCallLogDate, formatCallLogTime, durationCallLog, getHighlightedText } from '../../../Helpers/Utility';
import ProfileImage from '../../../Components/WebChat/Common/ProfileImage'
import {getFromLocalStorageAndDecrypt} from '../../WebChat/WebChatEncryptDecrypt';
import SDK from '../../SDK';
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";
import { getDataFromSDK, isLocalUser } from '../../../Helpers/Chat/User';
import { toast } from 'react-toastify';

const CallLogView = (props = {}) => {
    let { displayName, image, searchterm, callLog, emailId,initialName="", isAdminBlocked, isDeletedUser, callLog:{ fromUser="", toUser= "" } ={}, userId: propUserId} = props;
    const [callBehaviour, setCallBehaviour] = useState("call")
    const userId = propUserId || (isLocalUser(toUser) ? fromUser : toUser)
    const blockedList = useSelector(state => state.blockedContact?.data) ||[];
    const hideCallIcon = blockedList?.includes(userId);

    // The peer's picture is derived from the roster at render. On the receiver's side the
    // caller may not be a saved contact, so their profile (and image) isn't loaded — fetch
    // it once so the log shows their picture. WebCallLogs subscribes to rosterData, so the
    // completed fetch re-renders this row with the image. Guarded to fetch a peer only once.
    const profileRequested = useRef(false);
    useEffect(() => {
        const isOneToOne = callLog.callMode === "onetoone";
        if (image || !userId || !isOneToOne || isLocalUser(userId) || profileRequested.current) return;
        profileRequested.current = true;
        getDataFromSDK(userId).catch(() => { profileRequested.current = false; });
    }, [image, userId, callLog.callMode]);

    const onClick = useCallback(() => {
        if(isAdminBlocked || isDeletedUser || hideCallIcon) return;
        props.makeCall(callLog);
    })
    if (displayName && searchterm !== "" && !displayName.toLowerCase().includes(searchterm)) {
        return null;
    }

    const token = getFromLocalStorageAndDecrypt('token');
    let date = formatCallLogDate(callLog.callTime / 1000);
    let time = formatCallLogTime(callLog.callTime);
    let durationText = "";
    let showDuration = false;
    if (callLog.startTime && callLog.endTime) {
        durationText = durationCallLog(callLog.startTime, callLog.endTime);
        showDuration = true;
    }

    let callState = callLog.callState;
    // An incoming call that was never answered is a missed call, even though its
    // direction is recorded as incoming (callState 2). The server flags this via
    // missedStatus / a zero startTime, so honour those signals here to match the API
    // instead of showing it as a received incoming call.
    const isMissedStatus = typeof callLog.missedStatus === 'number' ? callLog.missedStatus > 0 : !!callLog.missedStatus;
    const notAnswered = !callLog.startTime || callLog.startTime === 0 || callLog.startTime === '0';
    if (callState === 2 && (isMissedStatus || notAnswered)) {
        callState = 0;
    }
    let callStateView = "";
    if (callState === 0) {
        callStateView = props.callLog.callType === "video" ? <IconVideoMissedCall /> : <IconAudioMissedCall/>;
    } else if (callState === 1) {
        callStateView = props.callLog.callType === "video" ? <IconVideoOutgoingCall /> : <IconAudioOutgoingCall/> ;
    } else {
        callStateView = props.callLog.callType === "video" ? <IconVideoIncommingCall /> : <IconAudioIncommingCall/> ;
    }

    const handleCallLogHover = () =>{
        const behaviourResponse = SDK.getCallBehaviour();
        if(behaviourResponse.data == "meet"){
            setCallBehaviour("meet")
        }
    }

    return <li className={`chat-list-li ${(isAdminBlocked || isDeletedUser) ? "pointer-default-all" : ""}`}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick}
    tabIndex="0"
    role="button"
    >
        <ProfileImage
            chatType={callLog.callMode === "onetoone" ? 'chat' : 'groupchat'}
            userToken={token}
            imageToken={isAdminBlocked ? "" : image}
            emailId={emailId}
            name={initialName}
            userId={userId}
        />
        <div className="recentchats">
            <div className="callInfo">
                <div className="recent-username-block">
                    <div className="recent-username">
                        <div className="username">
                            <h3 title={displayName}>
                                {searchterm ? getHighlightedText(displayName, searchterm) : displayName}
                                {isAdminBlocked && <span className="inactive-user-tag">(Inactive user)</span>}
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="CallStatus">
                    <i>
                        {callStateView}
                    </i>
                    <div className="callOn">
                        <span className="callOn">{date},</span> <span className="callTime">
                            {time}
                        </span>
                    </div>
                </div>
            </div>

            <div className="callAction" onMouseEnter={handleCallLogHover}> {/*NOSONAR*/}
                {showDuration &&
                    <span className="callDuration" >{durationText}</span>}
                {!hideCallIcon &&<i className={`callType ${(isAdminBlocked || isDeletedUser) ? 'calldisabled' : ''}`} style={{cursor:(isAdminBlocked || isDeletedUser || callBehaviour == 'meet') ? "not-allowed":"pointer" }}>
                    {props.callLog.callType === "video" ? <IconVideocall /> : <IconAudiocall />}
                </i>}
            </div>
        
        </div>


    </li>
}

export default CallLogView;

CallLogView.propTypes = {
    displayName: PropTypes.string,
    image: PropTypes.string,
    searchterm: PropTypes.string,
    callLog: PropTypes.shape({
        callTime: PropTypes.number.isRequired,
        startTime: PropTypes.number,
        endTime: PropTypes.number,
        callState: PropTypes.number, // not required, allow null
        callType: PropTypes.string.isRequired,
        callMode: PropTypes.string,
    }).isRequired,
    emailId: PropTypes.string,
    initialName: PropTypes.string,
    isAdminBlocked: PropTypes.bool,
    isDeletedUser: PropTypes.bool,
    makeCall: PropTypes.func.isRequired,
};
